<template>
  <v-skeleton-loader
    v-if="firstLoading"
    color="grey lighten-2"
    class="mx-auto"
    type="table-tbody"
  />
  <v-data-table 
    v-else
    :loading="loading"
    :headers="headers"
    :items="leaveApplications"
    :single-expand="true"
    show-expand
    item-key="id"
    class="elevation-1"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-toolbar-title>Leave Applications</v-toolbar-title>
        <v-tooltip
          content-class="bottom"
          bottom
          class="mr-10"
        >
          <template v-slot:activator="{ on }">
            <v-avatar 
              class="cursor-pointer"
              v-on="on"
              @click="loadLeaveApplications"                
            >
              <v-icon medium>
                mdi-refresh
              </v-icon>
            </v-avatar>
          </template>
          <span>Reload</span>
        </v-tooltip>
        <my-leave-application-department-dialog
          v-if="enableButton"
          :current-employee-uid="employeeUid"
        />
      </v-toolbar>
    </template>
    <template v-slot:item.status="{ item }">
      <span
        :class="{ 
          'warning--text': (item.latest_status.id === 100),
          'red--text': (item.latest_status.id === 500), 
          'grey--text': (item.latest_status.id === 501), 
          'green--text': (item.latest_status.id === 200) 
        }"
      >
        {{ item.latest_status.name }}
      </span>
    </template>
    <template v-slot:item.start_date="{ item }">
      {{ item.start_date }}
      {{ getDateWithLeaveSession(item, item.start_date) }}
    </template>
    <template v-slot:item.end_date="{ item }">
      {{ item.end_date }}
      {{ getDateWithLeaveSession(item, item.end_date) }}
    </template>
    <template v-slot:item.approver="{ item }">
      <!-- display approvers in ascending order based on level  -->
      <span
        v-for="(approval, i) in item.approvals"
        :key="i"
        :class="[
          item.approvals.length > 1 && item.latest_status.id !== 501 ? { 
            'warning--text': (approval.status.id === 100),
            'red--text': (approval.status.id === 500), 
          } : ''
        ]"
      >
        <template v-if="approval.approver && approval.approver.employee_uid !== employeeUid">
          <span v-if="i !== 0">  ,</span>
          {{ (approval.approver.user || {}).name || approval.updated_by }}          
        </template>
        <template v-if="!approval.approver && approval.approver_name">
          <span v-if="i !== 0">  ,</span>
          {{ approval.approver_name }}          
        </template>
      </span>
    </template>
    <template v-slot:item.action="{ item }">
      <my-leave-application-popover-menu-cancel
        :leave-application-id="item.id"
        :disabled="
          item.latest_status.id === 200 ||
            item.latest_status.id === 500 || 
            item.latest_status.id === 501 ||
            today >= item.start_date
        "
        @cancelled="cancel"
      />
    </template>
    <template v-slot:expanded-item="{ headers, item }">
      <td
        :colspan="headers.length"
        class="px-0"
      >        
        <v-container>          
          <v-row>
            <v-col cols="12">
              <v-card class="elevation-5">              
                <v-toolbar 
                  flat
                  color="white"
                >
                  <v-toolbar-title>Approval History</v-toolbar-title>
                </v-toolbar>
                <v-data-table 
                  :headers="approvalHeaders"
                  :items="item.approvals"
                  hide-default-footer
                  disable-pagination
                >
                  <template v-slot:item.approver="{ item }">
                    {{ item.approver ? item.approver.user.name : item.approver_name }}
                  </template>
                  <template v-slot:item.status="{ item }">
                    <span
                      :class="{ 
                        'warning--text': (item.status.id === 100),
                        'red--text': (item.status.id === 500), 
                        'grey--text': (item.status.id === 501), 
                        'green--text': (item.status.id === 200) 
                      }"
                    >
                      {{ item.status.name }}
                    </span>
                  </template>
                  <template v-slot:item.date="{ item }">
                    {{ $moment(item.created_at).format('YYYY-MM-DD') }}
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>          
          <v-row>
            <v-col
              v-if="item.documents.length > 0"
              cols="12"
            >
              <v-card-subtitle class="font-weight-bold white black--text pb-0">
                {{ item.documents.length }} documents
              </v-card-subtitle>
            </v-col>
            
            <v-col
              v-for="document in item.documents"
              :key="document.id"
              cols="12"
              class="pt-0"
            >
              <v-chip
                color="blue-grey lighten-3"
                link
                :href="document.file_url"
                target="_blank"
              >
                <v-icon
                  color="red"
                  left
                >
                  mdi-file-image
                </v-icon>
                <span class="text-truncate"> {{ document.file_name }} </span>
              </v-chip>
            </v-col>
          </v-row>
        </v-container>
      </td>
    </template>
  </v-data-table>
</template>

<script>
  export default {
    components: {
      MyLeaveApplicationPopoverMenuCancel: () => import('@/components/hris/my-leave/application/PopoverMenuCancel'),
      MyLeaveApplicationDepartmentDialog: () => import('@/components/hris/my-leave/application/DepartmentDialog'),
    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        firstLoading: false,
        loading: false,
        headers: [
          {
            text: 'Type',
            align: 'left',
            value: 'leave_type',
          },
          {
            text: 'Start Date',
            align: 'left',
            value: 'start_date',
          },
          {
            text: 'End Date',
            align: 'left',
            value: 'end_date',
          },
          {
            text: 'Days',
            align: 'left',
            value: 'requested_days',
          },
          {
            text: 'Reason',
            align: 'left',
            sortable: false,
            value: 'reason',
          },
          {
            text: 'Status',
            align: 'left',
            sortable: false,
            value: 'status',
          },
          {
            text: 'Approvers',
            align: 'left',
            sortable: false,
            value: 'approver',
          },
          {
            text: '',
            align: 'left',
            sortable: false,
            value: 'action',
          },
        ],
        approvalHeaders: [
          {
            text: 'Approver',
            align: 'left',
            sortable: false,
            value: 'approver',
          },
          {
            text: 'Status',
            align: 'left',
            sortable: false,
            value: 'status',
          },
          {
            text: 'Date',
            align: 'left',
            sortable: false,
            value: 'date',
          },
          {
            text: 'Remark',
            align: 'left',
            sortable: false,
            value: 'remark',
          },
        ],
        leaveApplications: [],
        departmentLeaveDashboards: [],
        enableButton: false,
      }
    },
    computed: {
      today () {
        return this.$moment().format('YYYY-MM-DD')
      },
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.firstLoading = true
            this.loadLeaveApplications()
            this.loadDepartmentLeaveDashboard()
          }
        },
      },
    },
    methods: {
      getDateWithLeaveSession (item, date) {
        if (date && item && item.leave_days) {
          var leaveSessionName = ''
          item.leave_days.forEach(leaveDay => {
            if (leaveDay.date === date) {
              if (leaveDay.leave_session_id === 4) { 
                leaveSessionName = '(Full Day)' 
              } else if (leaveDay.leave_session_id === 2) {
                leaveSessionName = '(AM)' 
              } else if (leaveDay.leave_session_id === 3) {
                leaveSessionName = '(PM)' 
              } else {
                leaveSessionName = '(None)' 
              }
            }
          })
          return leaveSessionName
        }
      },
      cancel () {
        this.loadLeaveApplications()
        this.$emit('cancelled')
      },
      loadLeaveApplications () {
        this.loading = true

        this.$api.leaveApplications().list(this.employeeUid)
          .then(response => {
            this.leaveApplications = []
            this.getLeaveApplications(response.data.filter(data => {
              return data.leave_entitlement.leave_type.is_apply_on_behalf === 0
            }))

            this.loading = false
            this.firstLoading = false
          })
      },
      getLeaveApplications (leaveApplications) {
        leaveApplications.map(application => {
          let approvals = ((application.approval_request || {}).approvals || [])
          
          const sortedApprovals = _.orderBy(approvals, ['created_at'], ['asc'])
          const latestApproval = _.orderBy(approvals, ['created_at'], ['desc'])[0]

          this.leaveApplications.push({ 
            ...application, 
            latest_status: latestApproval.status, 
            leave_type: application.leave_entitlement.leave_type.name, 
            approvals: sortedApprovals, 
          })
        })

        this.leaveApplications = _.orderBy(this.leaveApplications, ['latest_status.id', 'created_at'], ['asc', 'desc'])
      },
      loadDepartmentLeaveDashboard () {
        this.loading = true
        Promise.all([
          this.$api.employees().show(this.employeeUid),
          this.$api.departmentLeaveDashboards().list(),
          
        ]).then(([employee, leaveDashboardByDepartmentSetting]) => {
          let currentEmployee = employee.data

          this.departmentLeaveDashboards = leaveDashboardByDepartmentSetting.data
          this.departmentLeaveDashboards = this.departmentLeaveDashboards.filter(setting => setting.is_visible === 1)
          this.enableButton = this.departmentLeaveDashboards.some(setting => setting.department_id === currentEmployee.department_id)

          this.loading = false
        })
      },
    },
  }
</script>

<style scoped>
.cursor-pointer{
  cursor: pointer;
}
</style>
